import React, { useMemo } from 'react';
import { SectionTreeNodeDataProps } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { backgroundInlineStyles, BACKGROUND_SRC_DEFAULT_CLASSES } from './backgroundInlineStyles';
import { cn } from '../../utils/cn';
import { SLIDE_DECK_CANVAS } from './slideDeckCanvas';

type SectionProps = {
  id: string;
  children?: React.ReactNode;
  editorClassName?: string;
  staticAssetsBaseURL?: string | null;
  isEditor?: boolean;
  /** Render the section as a standalone active slide: 1920×1080 sizing, `group/slide` class, and `data-slide-active="true"` so child animations gated on the slide group resolve to their final state. Used during slide-deck PDF export. */
  renderAsSlide?: boolean;
} & SectionTreeNodeDataProps &
  React.ComponentPropsWithRef<React.ElementType>;

function SectionInner(
  {
    id,
    as,
    className,
    editorClassName,
    children,
    backgroundSrc,
    staticAssetsBaseURL,
    isEditor = false,
    renderAsSlide = false,
    // Consumed by the slide-deck renderer, which drops hidden slides before they reach here. Pulled
    // out of `props` so it never lands on the DOM as the HTML `hidden` attribute — that would blank
    // the slide in the editor canvas and in its rail thumbnail, where hidden slides must stay visible.
    hidden: _hidden,
    ...props
  }: SectionProps,
  ref: React.ComponentPropsWithRef<React.ElementType>['ref'],
) {
  const style = useMemo(() => {
    const base = backgroundInlineStyles(backgroundSrc, staticAssetsBaseURL);
    if (!renderAsSlide) return base;
    return {
      ...base,
      width: `${SLIDE_DECK_CANVAS.width}px`,
      height: `${SLIDE_DECK_CANVAS.height}px`,
      overflow: 'hidden',
      flexShrink: 0,
    };
  }, [backgroundSrc, staticAssetsBaseURL, renderAsSlide]);

  const slideActiveProps = renderAsSlide ? { 'data-slide-active': 'true' } : {};

  return (
    <section
      id={id}
      ref={ref}
      data-flexible-page-section
      {...slideActiveProps}
      className={cn(
        backgroundSrc ? BACKGROUND_SRC_DEFAULT_CLASSES : undefined,
        renderAsSlide ? 'group/slide' : undefined,
        className,
        editorClassName,
      )}
      {...props}
      style={style}
    >
      {children}
    </section>
  );
}

SectionInner.displayName = 'Section';

export const Section = React.forwardRef<React.ComponentPropsWithRef<React.ElementType>['ref'], SectionProps>(
  SectionInner,
);
